import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteBranding } from '../contexts/SiteBrandingContext';
import TranslatableText from './TranslatableText';
import { getBackendUrl } from '../config/api';

const StatsSection = () => {
  const { isDark } = useTheme();
  const { siteBranding } = useSiteBranding();
  const [stats, setStats] = useState({
    students: 0,
    teachers: 0,
    awards: 0,
    results: 0
  });
  
  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const BACKEND_URL = getBackendUrl();
      
      // Fetch site statistics
      const statsResponse = await fetch(`${BACKEND_URL}/api/site/statistics`);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className={`${isDark ? 'bg-dark-bg' : 'bg-gray-100'} py-12`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className={`text-3xl font-bold ${isDark ? 'text-dark-text' : 'text-gray-900'} mb-2`}>
            <TranslatableText text="School Statistics" />
          </h2>
          <p className={`text-lg ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
            <TranslatableText text="Our achievements in numbers" />
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <motion.div 
            className="text-center"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div 
              className="text-4xl lg:text-5xl font-bold mb-2"
              style={{ color: siteBranding.primary_color }}
            >
              {stats.students}+
            </div>
            <div className={`text-sm lg:text-base font-medium ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
              <TranslatableText text="Students" />
            </div>
          </motion.div>
          
          <motion.div 
            className="text-center"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div 
              className="text-4xl lg:text-5xl font-bold mb-2"
              style={{ color: siteBranding.primary_color }}
            >
              {stats.teachers}+
            </div>
            <div className={`text-sm lg:text-base font-medium ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
              <TranslatableText text="Teachers" />
            </div>
          </motion.div>
          
          <motion.div 
            className="text-center"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div 
              className="text-4xl lg:text-5xl font-bold mb-2"
              style={{ color: siteBranding.primary_color }}
            >
              {stats.awards}+
            </div>
            <div className={`text-sm lg:text-base font-medium ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
              <TranslatableText text="Awards" />
            </div>
          </motion.div>
          
          <motion.div 
            className="text-center"
            whileHover={{ scale: 1.05 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div 
              className="text-4xl lg:text-5xl font-bold mb-2"
              style={{ color: siteBranding.primary_color }}
            >
              {stats.results}%
            </div>
            <div className={`text-sm lg:text-base font-medium ${isDark ? 'text-dark-text-secondary' : 'text-gray-600'}`}>
              <TranslatableText text="Results" />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StatsSection;